import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Clock } from "lucide-react";
import { getDocMeta, listDocs, loadDocBody } from "../lib/docs";

interface DocsWikiLayoutProps {
  slug?: string;
}

/**
 * Wiki-style documentation layout: article list on the left, selected article
 * content on the right. `/docs` and `/docs/:slug` share this surface so the
 * docs entry point opens directly into reading instead of an overview card.
 */
export function DocsWikiLayout({ slug }: DocsWikiLayoutProps) {
  const { i18n, t } = useTranslation();
  const ordered = listDocs(i18n.language);
  const activeSlug = slug || ordered[0]?.slug || "";
  const meta = getDocMeta(activeSlug, i18n.language);
  const body = useMemo(
    () => (meta ? loadDocBody(activeSlug, i18n.language) : ""),
    [activeSlug, i18n.language, meta],
  );

  useEffect(() => {
    if (!meta) return;
    const previous = document.title;
    document.title = `${meta.title} · TokenBuddy Docs`;
    return () => {
      document.title = previous;
    };
  }, [meta]);

  if (!meta) {
    return (
      <div className="page-container py-20 text-center">
        <p className="text-[14px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
          404
        </p>
        <h1 className="mt-3 text-[28px] font-bold tracking-[0] text-[var(--color-ink-strong)]">
          {t("docs.article.notFoundTitle")}
        </h1>
        <p className="mt-2 text-[14px] text-[var(--color-text)]">
          {t("docs.article.notFoundBody", { slug: activeSlug })}
        </p>
        <Link
          to="/docs"
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-bold text-[var(--color-primary-deep)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t("docs.article.backToIndex")}
        </Link>
      </div>
    );
  }

  const idx = ordered.findIndex((article) => article.slug === activeSlug);
  const prev = idx > 0 ? ordered[idx - 1] : null;
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;

  return (
    <div className="bg-[var(--color-page)] py-8 md:py-10 xl:py-12">
      <div className="page-container grid gap-6 lg:grid-cols-[260px_minmax(0,820px)] lg:items-start xl:grid-cols-[280px_minmax(0,860px)]">
        <aside className="lg:sticky lg:top-20">
          <div className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] p-4 shadow-[0_10px_26px_rgba(60,41,112,0.04)]">
            <nav aria-label={t("docs.index.title")}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
                {t("docs.index.title")}
              </p>
              <ol className="mt-3 grid gap-1.5">
                {ordered.map((article) => {
                  const active = article.slug === activeSlug;
                  return (
                    <li key={article.slug}>
                      <Link
                        to={`/docs/${article.slug}`}
                        aria-current={active ? "page" : undefined}
                        className={[
                          "block rounded-[var(--radius-md)] border px-3 py-2.5 text-[13px] font-bold leading-5 transition-colors",
                          active
                            ? "border-[var(--color-line-strong)] bg-[var(--color-primary-soft)] text-[var(--color-primary-deep)]"
                            : "border-transparent text-[var(--color-ink-mid)] hover:border-[var(--color-line)] hover:bg-[var(--color-panel-subtle)]",
                        ].join(" ")}
                      >
                        {article.title}
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </aside>

        <article className="min-w-0 rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] px-5 py-7 shadow-[0_12px_36px_rgba(60,41,112,0.06)] md:px-8 md:py-9">
          <header className="border-b border-[var(--color-line)] pb-6">
            <h1 className="text-[34px] font-bold leading-[1.05] tracking-[0] text-[var(--color-ink-strong)] text-balance md:text-[44px]">
              {meta.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {meta.readMinutes} {t("docs.index.minutes")}
              </span>
              <span aria-hidden="true">·</span>
              <time dateTime={meta.updated}>{meta.updated}</time>
            </div>
          </header>

          <div
            className="doc-prose mt-8"
            dangerouslySetInnerHTML={{ __html: body }}
          />

          {(prev || next) && (
            <nav
              aria-label={t("docs.article.pagerLabel")}
              className="mt-12 grid gap-3 border-t border-[var(--color-line)] pt-6 sm:grid-cols-2"
            >
              {prev ? (
                <Link
                  to={`/docs/${prev.slug}`}
                  className="lift group flex flex-col rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] p-4"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
                    {t("docs.article.previous")}
                  </span>
                  <span className="mt-1.5 text-[14px] font-bold text-[var(--color-ink-strong)]">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <div aria-hidden="true" />
              )}
              {next ? (
                <Link
                  to={`/docs/${next.slug}`}
                  className="lift group flex flex-col rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] p-4 sm:text-right"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
                    {t("docs.article.next")}
                  </span>
                  <span className="mt-1.5 text-[14px] font-bold text-[var(--color-ink-strong)]">
                    {next.title}
                  </span>
                </Link>
              ) : (
                <div aria-hidden="true" />
              )}
            </nav>
          )}
        </article>
      </div>
    </div>
  );
}
